import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Login from "../../Authentication/Login";
import currencyData from "../Data/Currency.json";
import Header from "../../Common/Layout/Header";
import "./Invoice.css";
import PaymentMethodModel from "./PaymentMethodModel";
import CustomerFormModal from "../Master/CustomerModal";

const PlusIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 16 16"
  >
    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
  </svg>
);

const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 16 16"
  >
    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
    <path
      fillRule="evenodd"
      d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 16 16"
  >
    <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM2 2a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1H2z" />
    <path d="M2.5 4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5V4z" />
  </svg>
);

const EyeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 16 16"
  >
    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 1 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
  </svg>
);

const initialFieldValues = (user) => {
  const userCurrency = user?.currency
    ? currencyData.find((c) => c.code === user.currency) || currencyData[0]
    : currencyData[0];

  return {
    invoiceId: "00000000-0000-0000-0000-000000000000",
    userId: user?.id || "00000000-0000-0000-0000-000000000000",
    companyId: user?.cid || "00000000-0000-0000-0000-000000000000",
    from_name: user?.companyname || "",
    customerId: "",
    referenceId: "",
    referenceType: "SALE",
    paymentMethod: "CASH/BANK",
    invoiceNumber: "",
    invoiceDate: new Date().toISOString().split("T")[0],
    dueDate: "",
    status: "UNPAID",
    currency: userCurrency,
    items: [{ itemName: "", quantity: "", price: "" }],
    discountPercentage: "",
    appliedTaxes: [],
    notes: "",
    terms: "",
    bankAccountId: "",
    cheques: [],
    transactionType: "CASH/BANK",
  };
};

const EditSaleInvoice = () => {
  const { invoiceId } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const itemsTableRef = useRef(null);
  const invoiceDateRef = useRef(null);
  const dueDateRef = useRef(null);

  const [values, setValues] = useState(initialFieldValues(user));
  const [btnLoading, setBtnLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [pageLoading, setPageLoading] = useState(true);
  const [customerdata, setCustomerData] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [bankdata, setBankData] = useState([]);
  const [availableTaxes, setAvailableTaxes] = useState([]);
  const [servicedata, setServiceData] = useState([]);

  const [isTaxDropdownOpen, setIsTaxDropdownOpen] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(null);
  const [filteredServices, setFilteredServices] = useState([]);

  const [showPaymentModel, setShowPaymentModel] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState("CASH/BANK");
  const [selectedBankAccount, setSelectedBankAccount] = useState("");
  const [chequeDetails, setChequeDetails] = useState({
    bankAccountId: "",
    chequeNumber: "",
    issueDate: new Date().toISOString().split("T")[0],
    clearedDate: null,
    amount: 0,
    remarks: "",
    status: "Issued",
    userId: user?.id || "",
  });

  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerSearchTimeout, setCustomerSearchTimeout] = useState(null);
  const [searchMessage, setSearchMessage] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const paymentMethods = [
    { value: "CASH/BANK", label: "CASH/BANK" },
    { value: "UPI", label: "UPI" },
    { value: "BANKACCOUNT", label: "BANK ACCOUNT" },
    { value: "CHEQUE", label: "CHEQUE" },
    { value: "OTHERS", label: "OTHERS" },
  ];

  const {
    subtotal,
    discountAmount,
    taxableAmount,
    totalTaxAmount,
    grandTotal,
  } = useMemo(() => {
    const subtotal = values.items.reduce(
      (acc, item) =>
        acc + (Number(item.quantity) || 0) * (Number(item.price) || 0),
      0
    );
    const discountAmount =
      subtotal * ((Number(values.discountPercentage) || 0) / 100);
    const taxableAmount = subtotal - discountAmount;
    const totalTaxAmount = values.appliedTaxes.reduce((acc, tax) => {
      return acc + taxableAmount * ((Number(tax.rate) || 0) / 100);
    }, 0);
    const grandTotal = taxableAmount + totalTaxAmount;
    return {
      subtotal,
      discountAmount,
      taxableAmount,
      totalTaxAmount,
      grandTotal,
    };
  }, [values.items, values.discountPercentage, values.appliedTaxes]);

  useEffect(() => {
    setChequeDetails((prev) => ({
      ...prev,
      amount: grandTotal,
      userId: user ? user.id : prev.userId,
    }));
  }, [grandTotal, user]);

  useEffect(() => {
    if (user) {
      const userCurrency =
        currencyData.find((c) => c.code === user.currency) || currencyData[0];

      setValues((prev) => ({
        ...prev,
        from_name: user.companyname,
        companyId: user.cid,
        userId: user.id,
        currency: userCurrency,
        paymentMethod: "CASH/BANK",
        transactionType: "CASH/BANK",
      }));
      setChequeDetails((prev) => ({
        ...prev,
        userId: user.id,
      }));
      setSelectedPaymentMethod("CASH/BANK");
    }
  }, [user]);

  useEffect(() => {
    if (user && !loading) {
      GetInvoiceData();
      BankAccounts();
      Taxes();
      Items();
    }
  }, [user, loading, invoiceId]);

  useEffect(() => {
    if (values.invoiceDate) {
      const invoiceDate = new Date(values.invoiceDate);
      const dueDate = new Date(invoiceDate);
      dueDate.setDate(dueDate.getDate() + 1);

      setValues((prev) => ({
        ...prev,
        dueDate: dueDate.toISOString().split("T")[0],
      }));
    }
  }, [values.invoiceDate]);

  useEffect(() => {
    if (customerSearch.trim() === "") {
      setFilteredCustomers(customerdata);
    } else {
      const filtered = customerdata.filter((customer) =>
        customer.customerName
          .toLowerCase()
          .includes(customerSearch.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  }, [customerSearch, customerdata]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        itemsTableRef.current &&
        !itemsTableRef.current.contains(event.target)
      ) {
        setActiveSuggestionIndex(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (customerSearchTimeout) {
        clearTimeout(customerSearchTimeout);
      }
    };
  }, [customerSearchTimeout]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en", { month: "short" }).toUpperCase();
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const parseFormattedDate = (formattedDate) => {
    if (!formattedDate) return "";
    const parts = formattedDate.split("-");
    if (parts.length !== 3) return "";

    const months = {
      JAN: "01",
      FEB: "02",
      MAR: "03",
      APR: "04",
      MAY: "05",
      JUN: "06",
      JUL: "07",
      AUG: "08",
      SEP: "09",
      OCT: "10",
      NOV: "11",
      DEC: "12",
    };

    const day = parts[0];
    const month = months[parts[1]];
    const year = parts[2];

    if (!month) return "";

    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (name, value) => {
    const isoDate = parseFormattedDate(value);
    if (isoDate) {
      setValues((prev) => ({ ...prev, [name]: isoDate }));
    } else if (value === "") {
      setValues((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCalendarClick = (fieldName) => {
    if (fieldName === "invoiceDate" && invoiceDateRef.current) {
      invoiceDateRef.current.showPicker();
    } else if (fieldName === "dueDate" && dueDateRef.current) {
      dueDateRef.current.showPicker();
    }
  };

  const handleManualDateChange = (e, fieldName) => {
    const value = e.target.value;
    if (value) {
      setValues((prev) => ({ ...prev, [fieldName]: value }));
    }
  };

  const Customers = async (search = "") => {
    try {
      const searchParam =
        search !== undefined && search !== null ? search.trim() : "";
      const url = `${config.GETALLCUSTOMERSBYUSER}/${
        user.id
      }?search=${encodeURIComponent(searchParam)}`;

      console.log("API URL:", url);

      const res = await apiService.getAll(url);
      if (res) {
        setCustomerData(res);
        setFilteredCustomers(res);
        setHasSearched(true);
        setSearchMessage(
          res.length === 0 && search ? "No customers found" : ""
        );
      }
    } catch (err) {
      console.error("Error fetching customer data:", err);
      setSearchMessage("Error searching customers");
    }
  };

  const BankAccounts = async () => {
    try {
      const res = await apiService.get(
        `${config.GETALLBANKACCOUNTSBYUSER}/${user.id}`
      );
      if (res) setBankData(res);
    } catch (err) {
      console.error("Error fetching bank accounts:", err);
    }
  };

  const Items = async () => {
    try {
      const res = await apiService.getAll(
        `${config.GETITEMSBYACCOUNTTYPE}/${user.id}?accountType=Income`
      );
      if (res) setServiceData(res);
    } catch (err) {
      console.error("Error fetching income items:", err);
    }
  };

  const Taxes = async () => {
    try {
      const res = await apiService.get(
        `${config.GETALLTAXRATESBYUSER}/${user.id}`
      );
      if (res) setAvailableTaxes(res);
    } catch (err) {
      console.error("Error fetching taxes:", err);
    }
  };

  const debouncedCustomerSearch = useCallback((searchTerm) => {
    const trimmedSearch = searchTerm.trim();

    if (trimmedSearch.length >= 3) {
      setSearchMessage("Searching...");
      Customers(trimmedSearch);
    } else if (trimmedSearch === "") {
      setSearchMessage("");
      setCustomerData([]);
      setFilteredCustomers([]);
      setHasSearched(false);
    } else {
      setSearchMessage("Please enter at least 3 characters to search");
      setFilteredCustomers([]);
    }
  }, []);

  const GetInvoiceData = async () => {
    try {
      if (!invoiceId || invoiceId === "undefined") {
        console.error("Invalid invoice ID:", invoiceId);
        setPageLoading(false);
        return;
      }

      await Customers();

      const invoiceData = await apiService.getById(
        config.GETINVOICEBYID,
        invoiceId
      );

      if (invoiceData) {
        const updatedValues = {
          invoiceId: invoiceData.invoiceId || invoiceData.Id || invoiceId,
          userId: invoiceData.userId,
          companyId: invoiceData.companyId || user?.cid,
          from_name: user?.companyname || "",
          customerId: invoiceData.referenceId,
          referenceId: invoiceData.referenceId,
          referenceType: invoiceData.referenceType || "SALE",
          paymentMethod:
            invoiceData.transactionType === "CHEQUE"
              ? "CHEQUE"
              : invoiceData.transactionType === "BANKACCOUNT"
              ? "BANKACCOUNT"
              : invoiceData.transactionType === "UPI"
              ? "UPI"
              : invoiceData.transactionType === "OTHERS"
              ? "OTHERS"
              : "CASH/BANK",
          invoiceNumber: invoiceData.invoiceNumber,
          invoiceDate: invoiceData.invoiceDate
            ? invoiceData.invoiceDate.split("T")[0]
            : new Date().toISOString().split("T")[0],
          dueDate: invoiceData.dueDate ? invoiceData.dueDate.split("T")[0] : "",
          status: invoiceData.status || "UNPAID",
          currency:
            currencyData.find((c) => c.code === invoiceData.currencyCode) ||
            currencyData[0],
          items: invoiceData.items?.map((item) => ({
            itemName: item.description || "",
            quantity: item.quantity || "",
            price: item.unitPrice || "",
          })) || [{ itemName: "", quantity: "", price: "" }],
          discountPercentage:
            invoiceData.subtotal > 0
              ? (invoiceData.discount / invoiceData.subtotal) * 100
              : "",
          appliedTaxes: [],
          notes: invoiceData.notes || "",
          terms: invoiceData.terms || "",
          bankAccountId: invoiceData.bankAccountId || "",
          cheques: invoiceData.cheques || [],
          transactionType: invoiceData.transactionType || "CASH/BANK",
        };

        if (
          invoiceData.taxDetails &&
          typeof invoiceData.taxDetails === "object"
        ) {
          const taxes = Object.entries(invoiceData.taxDetails).map(
            ([name, rate]) => ({
              taxRateId: `temp-${name}`,
              taxName: name,
              rate: Number(rate) || "",
            })
          );
          updatedValues.appliedTaxes = taxes;
        }

        setValues(updatedValues);
        setSelectedPaymentMethod(updatedValues.paymentMethod);

        setTimeout(() => {
          const customer = customerdata.find(
            (c) => c.customerId === invoiceData.referenceId
          );
          if (customer) {
            setSelectedCustomer(customer);
            setCustomerSearch(customer.customerName);
          } else {
            if (invoiceData.customerDetails) {
              const customerFromResponse = invoiceData.customerDetails;
              setSelectedCustomer(customerFromResponse);
              setCustomerSearch(customerFromResponse.customerName);
            }
          }
        }, 100);

        if (invoiceData.cheques && invoiceData.cheques.length > 0) {
          setChequeDetails({
            ...invoiceData.cheques[0],
            userId: user.id,
          });
        }
      }
      setPageLoading(false);
    } catch (error) {
      console.error("Failed to fetch invoice data:", error);
      setPageLoading(false);
    }
  };

  const handleGenerateInvoiceNumber = async () => {
    try {
      setBtnLoading(true);
      const res = await apiService.get(
        `${config.GENERATEINVOICENUMBER}/${user.cid}`
      );

      if (res) {
        setValues((prev) => ({
          ...prev,
          invoiceNumber: res,
        }));
      } else {
        alert("Failed to generate invoice number.");
      }
    } catch (err) {
      console.error("Error generating invoice number:", err);
      alert("Error generating invoice number.");
    } finally {
      setBtnLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);
    setShowCustomerDropdown(true);

    if (customerSearchTimeout) {
      clearTimeout(customerSearchTimeout);
    }

    if (value.trim().length > 0 && value.trim().length < 3) {
      setSearchMessage("Please enter at least 3 characters to search");
      setFilteredCustomers([]);
    } else if (value.trim().length === 0) {
      setSearchMessage("");
      setFilteredCustomers([]);
    }

    const newTimeout = setTimeout(() => {
      debouncedCustomerSearch(value);
    }, 500);

    setCustomerSearchTimeout(newTimeout);
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.customerName);
    setShowCustomerDropdown(false);
    setSearchMessage("");

    setValues((prev) => ({
      ...prev,
      customerId: customer.customerId,
      referenceId: customer.customerId,
    }));
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearch("");
    setSearchMessage("");
    setHasSearched(false);
    setShowCustomerDropdown(false);
    setValues((prev) => ({
      ...prev,
      customerId: "",
      referenceId: "",
    }));
  };

  const handleItemChange = (index, event) => {
    const { name, value } = event.target;
    const updatedItems = [...values.items];

    if (name === "itemName") {
      updatedItems[index] = {
        ...updatedItems[index],
        [name]: value,
      };
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [name]: value,
      };
    }

    setValues((prev) => ({ ...prev, items: updatedItems }));

    if (name === "itemName" && value) {
      const filtered = servicedata
        .filter((service) =>
          service.itemName.toLowerCase().includes(value.toLowerCase())
        )
        .map((service) => service.itemName);
      setFilteredServices(filtered);
      setActiveSuggestionIndex(index);
    } else {
      setActiveSuggestionIndex(null);
    }
  };

  const handleSuggestionClick = (index, suggestion) => {
    const updatedItems = [...values.items];
    updatedItems[index].itemName = suggestion;
    setValues((prev) => ({ ...prev, items: updatedItems }));
    setActiveSuggestionIndex(null);
  };

  const handleAddItem = () =>
    setValues((prev) => ({
      ...prev,
      items: [...prev.items, { itemName: "", quantity: "", price: "" }],
    }));

  const handleRemoveItem = (index) => {
    if (values.items.length > 1) {
      const updatedItems = values.items.filter((_, i) => i !== index);
      setValues((prev) => ({ ...prev, items: updatedItems }));
    }
  };

  const handleCurrencyChange = (selectedCode) => {
    const newCurrency = currencyData.find((c) => c.code === selectedCode);
    if (newCurrency) setValues((prev) => ({ ...prev, currency: newCurrency }));
  };

  const handleAddTax = (taxToAdd) => {
    if (!values.appliedTaxes.some((t) => t.taxRateId === taxToAdd.taxRateId)) {
      setValues((prev) => ({
        ...prev,
        appliedTaxes: [
          ...prev.appliedTaxes,
          { ...taxToAdd, rate: taxToAdd.rate || "" },
        ],
      }));
    }
    setIsTaxDropdownOpen(false);
  };

  const handleRemoveTax = (taxIdToRemove) => {
    const updatedTaxes = values.appliedTaxes.filter(
      (t) => t.taxRateId !== taxIdToRemove
    );
    setValues((prev) => ({ ...prev, appliedTaxes: updatedTaxes }));
  };

  const handleTaxRateChange = (index, newRate) => {
    const updatedTaxes = values.appliedTaxes.map((tax, i) =>
      i === index ? { ...tax, rate: newRate } : tax
    );
    setValues((prev) => ({ ...prev, appliedTaxes: updatedTaxes }));
  };

  const handlePaymentMethodChange = (e) => {
    const method = e.target.value;
    setSelectedPaymentMethod(method);
    setValues((prev) => ({
      ...prev,
      paymentMethod: method,
      transactionType: method === "OTHERS" ? "OTHERS" : method,
    }));

    if (method === "BANKACCOUNT" || method === "CHEQUE") {
      setShowPaymentModel(true);
    } else {
      setValues((prev) => ({
        ...prev,
        bankAccountId: "",
        cheques: [],
      }));
    }
  };

  const handlePaymentModelSubmit = () => {
    if (selectedPaymentMethod === "BANKACCOUNT") {
      if (!selectedBankAccount) {
        alert("Please select a bank account");
        return;
      }
      setValues((prev) => ({
        ...prev,
        bankAccountId: selectedBankAccount,
      }));
    } else if (selectedPaymentMethod === "CHEQUE") {
      if (
        !chequeDetails.bankAccountId ||
        !chequeDetails.chequeNumber ||
        !chequeDetails.issueDate
      ) {
        alert("Please fill all required cheque details");
        return;
      }
      setValues((prev) => ({
        ...prev,
        bankAccountId: chequeDetails.bankAccountId,
        cheques: [
          {
            ...chequeDetails,
            clearedDate: chequeDetails.clearedDate || null,
            userId: user.id,
          },
        ],
      }));
    }
    setShowPaymentModel(false);
  };

  const handleCustomerSuccess = () => {
    setSearchMessage("Customer created successfully!");
    Customers();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validate = () => {
      const temp = {};
      temp.invoiceNumber = values.invoiceNumber.trim() !== "";
      temp.customerId = values.customerId !== "" && values.referenceId !== "";
      temp.dueDate = values.dueDate === "" ? false : true;
      temp.items =
        values.items.length > 0 &&
        values.items.every(
          (item) =>
            item.itemName &&
            (Number(item.quantity) || 0) > 0 &&
            (Number(item.price) || 0) >= 0
        );
      temp.paymentMethod = values.paymentMethod !== "";
      setErrors(temp);
      return Object.values(temp).every((x) => x === true);
    };

    if (!validate()) return;
    setBtnLoading(true);

    const payload = {
      invoiceId: values.invoiceId,
      userId: user.id,
      companyId: user.cid,
      referenceId: values.customerId,
      referenceType: "SALE",
      bankAccountId: values.bankAccountId || null,
      invoiceNumber: values.invoiceNumber,
      invoiceDate: values.invoiceDate,
      dueDate: values.dueDate || null,
      status: values.status,
      currencyCode: values.currency.code,
      subtotal,
      discount: discountAmount,
      tax: totalTaxAmount,
      grandTotal,
      notes: values.notes,
      terms: values.terms,
      taxDetails: values.appliedTaxes.reduce((acc, curr) => {
        acc[curr.taxName] = Number(curr.rate) || 0;
        return acc;
      }, {}),
      items: values.items.map((item) => ({
        description: item.itemName,
        quantity: Number(item.quantity) || 0,
        unitPrice: Number(item.price) || 0,
      })),
      transactionType: values.transactionType,
      cheques: values.cheques.map((cheque) => ({
        ...cheque,
        userId: user.id,
      })),
    };

    console.log("Updating invoice with referenceId:", payload.referenceId);

    try {
      const response = await apiService.update(config.UPDATEINVOICE, payload);
      navigate(`/editsaleinvoice/${values.invoiceId}`);
    } catch (error) {
      console.error("Error updating invoice:", error);
      alert("Failed to update invoice. Please try again.");
    } finally {
      setBtnLoading(false);
    }
  };

  const applyErrorClass = (field) =>
    field in errors && !errors[field] ? "invoice-form-control-danger" : "";

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-xl-12">
                <div className="card">
                  <div className="invoice-container">
                    <div className="invoice-row invoice-justify-content-center">
                      <div className="invoice-col-lg-12">
                        <div className="p-2">
                          <div className="page-header">
                            <div className="d-flex justify-content-between align-items-center ">
                              <div className="d-flex align-items-center">
                                <button
                                  onClick={() => navigate("/salelist")}
                                  className="btn btn-secondary me-3"
                                >
                                  Back To List
                                </button>
                                <h3 className="mb-0">Invoice Details</h3>
                              </div>
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                  if (invoiceId) {
                                    navigate(`/sale/view/${invoiceId}`);
                                  } else {
                                    console.error("Invoice ID is undefined");
                                    alert(
                                      "Cannot view invoice: Invoice ID is missing"
                                    );
                                  }
                                }}
                              >
                                <EyeIcon /> View Invoice
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="invoice-kanakku-page"></div>
                        <form onSubmit={handleSubmit} autoComplete="off">
                          <div className="invoice-card">
                            <div className="invoice-card-body">
                              <div className="invoice-row">
                                <div className="invoice-col-lg-6">
                                  <div className="invoice-form-group invoice-d-flex invoice-align-items-end invoice-gap-2">
                                    <div style={{ flex: 1 }}>
                                      <label>Invoice No:</label>
                                      <input
                                        name="invoiceNumber"
                                        type="text"
                                        className={`invoice-form-control invoice-form-control-sm ${applyErrorClass(
                                          "invoiceNumber"
                                        )}`}
                                        placeholder="e.g., INV-001"
                                        value={values.invoiceNumber}
                                        onChange={handleInputChange}
                                      />
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn-primary"
                                      onClick={handleGenerateInvoiceNumber}
                                    >
                                      Generate Invoice No
                                    </button>
                                  </div>

                                  <div className="invoice-form-group">
                                    <label>Bill From:</label>
                                    <input
                                      type="text"
                                      className="invoice-form-control"
                                      value={values.from_name || ""}
                                      readOnly
                                    />
                                  </div>
                                  <div className="invoice-form-group">
                                    <div className="invoice-d-flex invoice-justify-content-between invoice-align-items-center invoice-mb-2">
                                      <label>Bill To:</label>
                                      <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() =>
                                          setShowCustomerModal(true)
                                        }
                                      >
                                        <PlusIcon /> Add New Customer
                                      </button>
                                    </div>
                                    {selectedCustomer ? (
                                      <div className="invoice-customer-details-box">
                                        <strong>
                                          {selectedCustomer.customerName}
                                        </strong>
                                        <p>{selectedCustomer.phoneNumber}</p>
                                        <p>{selectedCustomer.address}</p>
                                        <p>{`${selectedCustomer.city}, ${selectedCustomer.state} - ${selectedCustomer.postalCode}`}</p>
                                        <button
                                          type="button"
                                          className="invoice-btn-change-customer"
                                          onClick={handleClearCustomer}
                                        >
                                          Change
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="invoice-position-relative">
                                        <div className="invoice-input-group">
                                          <input
                                            type="text"
                                            className={`invoice-form-control ${applyErrorClass(
                                              "customerId"
                                            )}`}
                                            placeholder="Enter customer name ..."
                                            value={customerSearch}
                                            onChange={
                                              handleCustomerSearchChange
                                            }
                                            onFocus={() =>
                                              setShowCustomerDropdown(true)
                                            }
                                          />
                                          {customerSearch && (
                                            <button
                                              type="button"
                                              className="invoice-btn invoice-btn-danger"
                                              onClick={handleClearCustomer}
                                              title="Clear search"
                                            >
                                              ×
                                            </button>
                                          )}
                                        </div>

                                        {searchMessage && (
                                          <div className="invoice-search-message invoice-text-muted invoice-small invoice-mt-1">
                                            {searchMessage}
                                          </div>
                                        )}

                                        {showCustomerDropdown &&
                                          filteredCustomers.length > 0 && (
                                            <div className="invoice-customer-dropdown">
                                              {filteredCustomers.map(
                                                (customer) => (
                                                  <div
                                                    key={customer.customerId}
                                                    className="invoice-customer-dropdown-item"
                                                    onClick={() =>
                                                      handleCustomerSelect(
                                                        customer
                                                      )
                                                    }
                                                  >
                                                    <strong>
                                                      {customer.customerName}
                                                    </strong>
                                                    <div className="invoice-text-muted invoice-small">
                                                      {customer.email} |{" "}
                                                      {customer.phoneNumber}
                                                    </div>
                                                    <div className="invoice-text-muted invoice-small">
                                                      {customer.address},{" "}
                                                      {customer.city}
                                                    </div>
                                                  </div>
                                                )
                                              )}
                                            </div>
                                          )}
                                      </div>
                                    )}
                                    {errors.customerId === false ? (
                                      <div className="invoice-validationerror">
                                        Please select customer
                                      </div>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                </div>
                                <div className="invoice-col-lg-6">
                                  <div className="invoice-form-group">
                                    <label>Invoice Date:</label>
                                    <div className="invoice-date-input-group">
                                      <input
                                        type="text"
                                        className="invoice-form-control invoice-date-display"
                                        placeholder="DD-MMM-YYYY"
                                        value={formatDate(values.invoiceDate)}
                                        onChange={(e) =>
                                          handleDateChange(
                                            "invoiceDate",
                                            e.target.value
                                          )
                                        }
                                      />
                                      <input
                                        ref={invoiceDateRef}
                                        type="date"
                                        className="invoice-form-control invoice-date-picker"
                                        value={values.invoiceDate}
                                        onChange={(e) =>
                                          handleManualDateChange(
                                            e,
                                            "invoiceDate"
                                          )
                                        }
                                      />
                                      <button
                                        type="button"
                                        className="invoice-calendar-btn"
                                        onClick={() =>
                                          handleCalendarClick("invoiceDate")
                                        }
                                      >
                                        <CalendarIcon />
                                      </button>
                                    </div>
                                  </div>
                                  <div className="invoice-form-group">
                                    <label>Due Date:</label>
                                    <div className="invoice-date-input-group">
                                      <input
                                        type="text"
                                        className="invoice-form-control invoice-date-display"
                                        placeholder="DD-MMM-YYYY"
                                        value={formatDate(values.dueDate)}
                                        onChange={(e) =>
                                          handleDateChange(
                                            "dueDate",
                                            e.target.value
                                          )
                                        }
                                      />
                                      <input
                                        ref={dueDateRef}
                                        type="date"
                                        className="invoice-form-control invoice-date-picker"
                                        value={values.dueDate}
                                        onChange={(e) =>
                                          handleManualDateChange(e, "dueDate")
                                        }
                                      />
                                      <button
                                        type="button"
                                        className="invoice-calendar-btn"
                                        onClick={() =>
                                          handleCalendarClick("dueDate")
                                        }
                                      >
                                        <CalendarIcon />
                                      </button>
                                    </div>
                                    {errors.dueDate === false ? (
                                      <div className="invoice-validationerror">
                                        Please select date
                                      </div>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                  <div className="invoice-form-group">
                                    <label>Status:</label>
                                    <select
                                      name="status"
                                      className="invoice-form-select"
                                      value={values.status}
                                      onChange={handleInputChange}
                                    >
                                      <option value="UNPAID">UNPAID</option>
                                      <option value="PAID">PAID</option>
                                    </select>
                                  </div>
                                  <div className="invoice-form-group">
                                    <label>Currency:</label>
                                    <select
                                      className="invoice-form-select"
                                      value={values.currency.code}
                                      onChange={(e) =>
                                        handleCurrencyChange(e.target.value)
                                      }
                                    >
                                      {currencyData.map((opt) => (
                                        <option key={opt.code} value={opt.code}>
                                          {opt.name} ({opt.code})
                                        </option>
                                      ))}
                                    </select>
                                    <small className="invoice-text-muted">
                                      company currency: {user.currency}
                                    </small>
                                  </div>
                                </div>
                              </div>

                              <div
                                className="invoice-items-table-container"
                                ref={itemsTableRef}
                              >
                                <table className="invoice-items-table">
                                  <thead>
                                    <tr>
                                      <th className="invoice-item-description">
                                        Item Description
                                      </th>
                                      <th className="invoice-item-quantity">
                                        Quantity
                                      </th>
                                      <th className="invoice-item-price">
                                        Price
                                      </th>
                                      <th className="invoice-item-amount">
                                        Amount
                                      </th>
                                      <th className="invoice-item-action">
                                        Action
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {values.items.map((item, index) => (
                                      <tr key={index}>
                                        <td className="invoice-item-description">
                                          <div className="invoice-autocomplete-wrapper">
                                            <input
                                              name="itemName"
                                              type="text"
                                              className="invoice-form-control invoice-item-input"
                                              placeholder="Enter item description..."
                                              value={item.itemName}
                                              onChange={(e) =>
                                                handleItemChange(index, e)
                                              }
                                            />
                                            {activeSuggestionIndex === index &&
                                              filteredServices.length > 0 && (
                                                <ul className="invoice-suggestions-list">
                                                  {filteredServices.map(
                                                    (suggestion) => (
                                                      <li
                                                        key={suggestion}
                                                        onClick={() =>
                                                          handleSuggestionClick(
                                                            index,
                                                            suggestion
                                                          )
                                                        }
                                                      >
                                                        {suggestion}
                                                      </li>
                                                    )
                                                  )}
                                                </ul>
                                              )}
                                            {errors.items === false ? (
                                              <div className="invoice-validationerror">
                                                Please select item
                                              </div>
                                            ) : (
                                              ""
                                            )}
                                          </div>
                                        </td>
                                        <td className="invoice-item-quantity">
                                          <input
                                            name="quantity"
                                            type="number"
                                            className="invoice-form-control invoice-item-input-small"
                                            placeholder="1"
                                            value={item.quantity}
                                            onChange={(e) =>
                                              handleItemChange(index, e)
                                            }
                                          />
                                        </td>
                                        <td className="invoice-item-price">
                                          <input
                                            name="price"
                                            type="number"
                                            className="invoice-form-control invoice-item-input-small"
                                            placeholder="0.00"
                                            value={item.price}
                                            onChange={(e) =>
                                              handleItemChange(index, e)
                                            }
                                          />
                                          {errors.items === false ? (
                                            <div className="invoice-validationerror">
                                              Please enter amount
                                            </div>
                                          ) : (
                                            ""
                                          )}
                                        </td>
                                        <td className="invoice-item-amount invoice-item-amount">{`${
                                          values.currency.code
                                        } ${(
                                          (Number(item.quantity) || 0) *
                                          (Number(item.price) || 0)
                                        ).toFixed(2)}`}</td>
                                        <td className="invoice-item-action">
                                          {values.items.length > 1 && (
                                            <button
                                              type="button"
                                              className="invoice-btn-remove"
                                              onClick={() =>
                                                handleRemoveItem(index)
                                              }
                                            >
                                              <TrashIcon />
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <div className="invoice-add-item-button-container">
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={handleAddItem}
                                >
                                  <PlusIcon /> Add New Item
                                </button>
                              </div>

                              <div className="invoice-bottom-section invoice-row">
                                <div className="invoice-col-lg-7">
                                  <div className="invoice-form-group">
                                    <label>
                                      Payment Method{" "}
                                      <span className="invoice-text-danger">
                                        *
                                      </span>
                                    </label>
                                    <select
                                      name="paymentMethod"
                                      className={`invoice-form-select ${applyErrorClass(
                                        "paymentMethod"
                                      )}`}
                                      value={values.paymentMethod}
                                      onChange={handlePaymentMethodChange}
                                    >
                                      <option value="CASH/BANK">
                                        CASH/BANK
                                      </option>
                                      {paymentMethods
                                        .filter(
                                          (method) =>
                                            method.value !== "CASH/BANK"
                                        )
                                        .map((method) => (
                                          <option
                                            key={method.value}
                                            value={method.value}
                                          >
                                            {method.label}
                                          </option>
                                        ))}
                                    </select>
                                    {errors.paymentMethod === false && (
                                      <div className="invoice-validationerror">
                                        Please select a payment method
                                      </div>
                                    )}
                                  </div>

                                  {values.paymentMethod === "BANKACCOUNT" &&
                                    values.bankAccountId && (
                                      <div className="invoice-payment-details invoice-mb-3">
                                        <p>
                                          <strong>Selected Bank:</strong>{" "}
                                          {
                                            bankdata.find(
                                              (b) =>
                                                b.bankAccountId ===
                                                values.bankAccountId
                                            )?.bankName
                                          }{" "}
                                          (Account:{" "}
                                          {
                                            bankdata.find(
                                              (b) =>
                                                b.bankAccountId ===
                                                values.bankAccountId
                                            )?.accountNumber
                                          }
                                          )
                                        </p>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-secondary"
                                          onClick={() => {
                                            setSelectedPaymentMethod(
                                              "BANKACCOUNT"
                                            );
                                            setShowPaymentModel(true);
                                          }}
                                        >
                                          Change Bank Account
                                        </button>
                                      </div>
                                    )}

                                  {values.paymentMethod === "CHEQUE" &&
                                    values.cheques.length > 0 && (
                                      <div className="invoice-payment-details invoice-mb-3">
                                        <p>
                                          <strong>Cheque Details:</strong>
                                        </p>
                                        <ul className="invoice-list-unstyled">
                                          <li>
                                            Cheque No:{" "}
                                            {values.cheques[0].chequeNumber}
                                          </li>
                                          <li>
                                            Bank:{" "}
                                            {
                                              bankdata.find(
                                                (b) =>
                                                  b.bankAccountId ===
                                                  values.cheques[0]
                                                    .bankAccountId
                                              )?.bankName
                                            }
                                          </li>
                                          <li>
                                            Amount: {values.currency.code}{" "}
                                            {values.cheques[0].amount.toFixed(
                                              2
                                            )}
                                          </li>
                                          <li>
                                            Issue Date:{" "}
                                            {new Date(
                                              values.cheques[0].issueDate
                                            ).toLocaleDateString()}
                                          </li>
                                          {values.cheques[0].clearedDate && (
                                            <li>
                                              Cleared Date:{" "}
                                              {new Date(
                                                values.cheques[0].clearedDate
                                              ).toLocaleDateString()}
                                            </li>
                                          )}
                                          <li>
                                            Status: {values.cheques[0].status}
                                          </li>
                                        </ul>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-secondary"
                                          onClick={() => {
                                            setSelectedPaymentMethod("CHEQUE");
                                            setChequeDetails(values.cheques[0]);
                                            setShowPaymentModel(true);
                                          }}
                                        >
                                          Edit Cheque Details
                                        </button>
                                      </div>
                                    )}

                                  <div className="invoice-form-group">
                                    <label>Notes</label>
                                    <textarea
                                      name="notes"
                                      className="invoice-form-control"
                                      placeholder="Any additional notes..."
                                      value={values.notes}
                                      onChange={handleInputChange}
                                    ></textarea>
                                  </div>
                                  <div className="invoice-form-group">
                                    <label>Terms & Conditions</label>
                                    <textarea
                                      name="terms"
                                      className="invoice-form-control"
                                      placeholder="Payment terms..."
                                      value={values.terms}
                                      onChange={handleInputChange}
                                    ></textarea>
                                  </div>
                                </div>
                                <div className="invoice-col-lg-5">
                                  <div className="invoice-summary">
                                    <div className="invoice-summary-row">
                                      <span>Sub Total</span>
                                      <span>{`${
                                        values.currency.code
                                      } ${subtotal.toFixed(2)}`}</span>
                                    </div>
                                    <div className="invoice-summary-row">
                                      <span>Discount (%)</span>
                                      <div className="invoice-discount-control">
                                        <input
                                          type="number"
                                          className="invoice-form-control-summary"
                                          placeholder="0"
                                          value={values.discountPercentage}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            setValues((prev) => ({
                                              ...prev,
                                              discountPercentage: value,
                                            }));
                                          }}
                                        />
                                        <span>
                                          -{" "}
                                          {`${
                                            values.currency.code
                                          } ${discountAmount.toFixed(2)}`}
                                        </span>
                                      </div>
                                    </div>
                                    {values.appliedTaxes.map((tax, index) => {
                                      const taxValue =
                                        taxableAmount *
                                        ((Number(tax.rate) || 0) / 100);
                                      return (
                                        <div
                                          key={tax.taxRateId}
                                          className="invoice-summary-row"
                                        >
                                          <span>{tax.taxName} (%)</span>
                                          <div className="invoice-tax-control">
                                            <input
                                              type="number"
                                              className="invoice-form-control-summary"
                                              placeholder="0"
                                              value={tax.rate}
                                              onChange={(e) =>
                                                handleTaxRateChange(
                                                  index,
                                                  e.target.value
                                                )
                                              }
                                            />
                                            <span>
                                              +{" "}
                                              {`${
                                                values.currency.code
                                              } ${taxValue.toFixed(2)}`}
                                            </span>
                                            <button
                                              type="button"
                                              className="invoice-btn-remove-tax"
                                              onClick={() =>
                                                handleRemoveTax(tax.taxRateId)
                                              }
                                            >
                                              <TrashIcon />
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                    <div className="invoice-summary-row invoice-add-tax-container">
                                      <div className="invoice-dropdown">
                                        <button
                                          type="button"
                                          className="invoice-btn-add-tax"
                                          onClick={() =>
                                            setIsTaxDropdownOpen(
                                              !isTaxDropdownOpen
                                            )
                                          }
                                        >
                                          <PlusIcon /> Add Tax
                                        </button>
                                        {isTaxDropdownOpen && (
                                          <div className="invoice-dropdown-menu invoice-show">
                                            {availableTaxes.filter(
                                              (availTax) =>
                                                !values.appliedTaxes.some(
                                                  (appliedTax) =>
                                                    appliedTax.taxRateId ===
                                                    availTax.taxRateId
                                                )
                                            ).length > 0 ? (
                                              availableTaxes
                                                .filter(
                                                  (availTax) =>
                                                    !values.appliedTaxes.some(
                                                      (appliedTax) =>
                                                        appliedTax.taxRateId ===
                                                        availTax.taxRateId
                                                    )
                                                )
                                                .map((tax) => (
                                                  <button
                                                    key={tax.taxRateId}
                                                    className="invoice-dropdown-item"
                                                    type="button"
                                                    onClick={() =>
                                                      handleAddTax(tax)
                                                    }
                                                  >
                                                    {tax.taxName} @ {tax.rate}%
                                                  </button>
                                                ))
                                            ) : (
                                              <span className="invoice-dropdown-item-text">
                                                No more taxes to add
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="invoice-summary-total">
                                      <span>Grand Total</span>
                                      <span>{`${
                                        values.currency.code
                                      } ${grandTotal.toFixed(2)}`}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="invoice-form-actions">
                            {btnLoading ? (
                              <button
                                type="submit"
                                className="btn btn-success"
                                disabled
                              >
                                Updating...
                              </button>
                            ) : (
                              <button type="submit" className="btn btn-success">
                                Update Invoice
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() => navigate(-1)}
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PaymentMethodModel
        show={showPaymentModel}
        handleClose={() => setShowPaymentModel(false)}
        paymentMethod={selectedPaymentMethod}
        bankAccounts={bankdata}
        chequeDetails={chequeDetails}
        setChequeDetails={setChequeDetails}
        selectedBankAccount={selectedBankAccount}
        setSelectedBankAccount={setSelectedBankAccount}
        handleSubmit={handlePaymentModelSubmit}
        currencyCode={values.currency.code}
        userId={user.id}
      />

      <CustomerFormModal
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
        onSuccess={handleCustomerSuccess}
        mode="add"
      />
    </div>
  );
};

export default EditSaleInvoice;
